import React, { useState } from 'react';

/**
 * An email body, rendered safely. Shared by the classification review page and
 * the mailbox viewer.
 *
 * The default class names are the review page's (scoped under `.creport` in
 * ClassificationReview.css); other pages pass their own.
 */

// Body rendering, ported from report.py's fillDetail so the two read the same.
// Stored bodies are Graph's `body.content` verbatim — for HTML mail that is
// markup, despite the field being called body_text — so a reviewer judging a
// marketing email needs it rendered, not escaped into a wall of <style> blocks.
const HTML_BODY = /<[a-z!][\s\S]*>/i;

// Per-email cap, matching report.py's _MAX_BODY_CHARS: a few huge marketing
// emails should not put a megabyte of markup in the DOM. The Mailbox shows one
// message whole and passes `maxChars={null}`: its body carries its inline
// images as data URLs, which a cut would break mid-image.
const MAX_BODY_CHARS = 50000;

// The whole reason an email can be rendered at all. `default-src 'none'` blocks
// every remote fetch the message attempts, so **tracking pixels and beacons
// never fire when a row is expanded** — opening a spam row to judge it must not
// tell the sender a human read it. Inline styles and data: images still render,
// which is what an email client's "images off" default looks like.
const csp = (remoteImages) =>
  '<meta http-equiv="Content-Security-Policy" content="' +
  `default-src 'none'; img-src data:${remoteImages ? ' https:' : ''}; ` +
  "style-src 'unsafe-inline' data:; font-src data:; media-src data:;\">";

// Whether the message asks for pictures from the internet that a person could
// see: an <img> with an http(s) source, or a background image in markup or
// inline style. Only then is "Show images" offered. An <img> declared one pixel
// (or none) wide or high, or hidden by its style, is a tracking pixel and does
// not count: showing it changes nothing on screen, so the line would only say
// there are images to see when there are not.
const IMG_TAG = /<img\b[^>]*>/gi;
const REMOTE_SRC = /\ssrc\s*=\s*["']?\s*https?:/i;
const TINY_ATTR = /\s(?:width|height)\s*=\s*["']?\s*0*[01](?:px)?\s*["'\s>/]/i;
const TINY_STYLE = /\sstyle\s*=\s*["'][^"']*(?:(?:width|height)\s*:\s*0*[01](?:px|pt)?\s*(?:;|!|["'])|display\s*:\s*none|visibility\s*:\s*hidden)/i;
const REMOTE_BACKGROUND = /url\(\s*["']?\s*https?:|\sbackground\s*=\s*["']?\s*https?:/i;
export const hasRemoteImages = (body) => {
  const html = body || '';
  if (REMOTE_BACKGROUND.test(html)) return true;
  for (const tag of html.match(IMG_TAG) || []) {
    if (REMOTE_SRC.test(tag) && !TINY_ATTR.test(tag) && !TINY_STYLE.test(tag)) return true;
  }
  return false;
};

// A floor under the body copy. Plenty of business mail ships paragraphs at 11 or
// 12px, which is fine in a mail client filling a window and too small in a
// column beside a form.
//
// `max(15px, 1em)` rather than a flat size, because 1em inside a `p` is what the
// paragraph *inherited*, not what it set: a message whose container is already
// larger keeps its own scale, and only the ones below the floor get raised. The
// `!important` is unavoidable — the whole problem is emails that set a small
// size themselves, and without it their own rule wins. What it costs is a
// paragraph deliberately styled bigger than its surroundings, which gets pulled
// back to the inherited size; headings are untouched, since they are not `p`.
//
// The review page keeps that default. The Mailbox, where the message is the page
// rather than a column beside a form, asks for a larger floor over the elements
// email text actually sits in (`readable`): most mail sets its copy on a div, a
// table cell or a span, not a p. `max(…, 1em)` does not compound down a nested
// chain: each element is raised to the floor or keeps what it inherited.
const BODY_STYLE = '<style>p{font-size:max(15px,1em)!important}</style>';
const READABLE_STYLE =
  '<style>p,div,td,th,span,li,font,blockquote{font-size:max(16px,1em)!important}' +
  'body{line-height:1.5}</style>';

const withCsp = (body, readable, remoteImages) => {
  const head = csp(remoteImages) + (readable ? READABLE_STYLE : BODY_STYLE);
  return /<head[^>]*>/i.test(body) ? body.replace(/<head[^>]*>/i, (m) => m + head) : head + body;
};

// Fit the frame to its content, then again once late images have settled.
// Wrapped because a browser may refuse contentDocument on an opaque origin, in
// which case the taller CSS default stands in.
const fitFrame = (e) => {
  const frame = e.currentTarget;
  const measure = () => {
    try {
      const d = frame.contentDocument;
      const h = Math.max(d.body?.scrollHeight || 0, d.documentElement?.scrollHeight || 0);
      if (h) frame.style.height = `${Math.min(Math.max(h + 8, 160), 1400)}px`;
    } catch {
      /* opaque origin: keep the CSS default height */
    }
  };
  measure();
  setTimeout(measure, 300);
};

/**
 * The email as the customer sent it.
 *
 * The sandbox omits `allow-scripts`, so nothing in the message executes;
 * `allow-same-origin` is present only so the height can be measured. Both that
 * and the CSP are belt and braces — either alone would stop a tracking pixel,
 * and neither is worth removing.
 *
 * With `imagesToggle`, a message that asks for pictures from the internet gets
 * a "Show images" line above it (the Mailbox). Showing them lets the frame
 * fetch `https:` images for this message only, which is what a mail client's
 * "Download pictures" does, and tells the sender's server that someone opened
 * it: the choice is the reader's, each time. The review page never offers it;
 * a reviewer judging spam must not tell the sender a human read it. Give the
 * component a `key` per message so the choice does not carry over.
 */
const EmailBody = ({
  body,
  textClassName = 'body',
  frameClassName = 'bodyframe',
  readable = false,
  maxChars = MAX_BODY_CHARS,
  imagesToggle = false,
}) => {
  const [showImages, setShowImages] = useState(false);
  if (!body) return <div className={textClassName}>(no body)</div>;
  const capped =
    maxChars && body.length > maxChars ? `${body.slice(0, maxChars)}\n\n[… truncated …]` : body;
  if (!HTML_BODY.test(capped)) {
    return <div className={textClassName}>{capped.replace(/\n{3,}/g, '\n\n').trim()}</div>;
  }
  const offer = imagesToggle && hasRemoteImages(capped);
  const remote = offer && showImages;
  return (
    <>
      {offer && (
        <div className="mb-1 flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-600">
          <span className="flex-1">
            {remote ? 'Showing images from the internet.' : 'Images from the internet are not shown.'}
          </span>
          <button
            type="button"
            onClick={() => setShowImages((v) => !v)}
            className="shrink-0 font-medium text-blue-700 hover:underline"
          >
            {remote ? 'Hide images' : 'Show images'}
          </button>
        </div>
      )}
      <iframe
        className={frameClassName}
        title="Original email"
        sandbox="allow-same-origin"
        referrerPolicy="no-referrer"
        srcDoc={withCsp(capped, readable, remote)}
        onLoad={fitFrame}
      />
    </>
  );
};

export default EmailBody;
